import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { PhaseType } from '@schema/phase'
import {
  getListedCreationWorkflowSteps,
  InitiativeCreationStep,
} from '../../shared/workflow'
import { createContext, ReactNode, useContext, useRef } from 'react'
import { Subtitle, Title } from '@app/components/core/typography/Title'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { enrich } from '@i18n/localizeHelper'
import { StepProgressDots } from './StepProgressDots'
import { stepProgressDotsHeight } from './stepProgressDotsHeight'
import type { Style } from 'inlines'

const STEP_CONTENT_WIDTH = '30rem'
const STEP_GUIDE_DOTS_WIDTH = '2rem'
const STEP_GUIDE_GAP = '3rem'
const STEP_GUIDE_WIDTH = `calc(${STEP_GUIDE_DOTS_WIDTH} + ${STEP_GUIDE_GAP} + ${STEP_CONTENT_WIDTH})`

const CreationWorkflowOptionsContext = createContext<{
  skipPhaseSelection: boolean
  stepProgressPersistedIndexRef: React.MutableRefObject<number | undefined>
} | null>(null)

export const CreationWorkflowOptionsProvider = ({
  skipPhaseSelection,
  children,
}: {
  skipPhaseSelection?: boolean
  children: ReactNode
}) => {
  const stepProgressPersistedIndexRef = useRef<number | undefined>(undefined)

  return (
    <CreationWorkflowOptionsContext.Provider
      value={{
        skipPhaseSelection: skipPhaseSelection ?? false,
        stepProgressPersistedIndexRef,
      }}
    >
      {children}
    </CreationWorkflowOptionsContext.Provider>
  )
}

const fixedGuideStyle: Style = {
  position: 'fixed',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  left: 'max(1rem, calc((100vw - min(77.5rem, 85vw)) / 2 + 5rem))',
  '@media (max-width: 1400px)': {
    left: 'max(1rem, calc((100vw - min(77.5rem, 85vw)) / 2))',
  },
}

export const CreationStepLayout = ({
  selectedPhaseType,
  currentStep,
  titleOverride,
  descriptionOverride,
  children,
}: {
  selectedPhaseType: PhaseType | null
  currentStep: InitiativeCreationStep
  titleOverride?: string
  descriptionOverride?: string | null
  children: ReactNode
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const workflowOptions = useContext(CreationWorkflowOptionsContext)
  const skipPhaseSelection = workflowOptions?.skipPhaseSelection ?? false
  const listedWorkflowSteps = selectedPhaseType
    ? getListedCreationWorkflowSteps(selectedPhaseType, { skipPhaseSelection })
    : []
  const currentStepIndex = listedWorkflowSteps.indexOf(currentStep)
  const stepNumber = currentStepIndex + 1
  const title = titleOverride ?? translations.creationStepTitles[currentStep]
  const description =
    descriptionOverride !== undefined
      ? descriptionOverride
      : translations.creationStepDescriptions[currentStep]

  const dotsHeight = stepProgressDotsHeight(listedWorkflowSteps.length)

  const stepGuideHeader = (
    <Row gap="3rem" alignItems="stretch" style={fixedGuideStyle}>
      <StepProgressDots
        total={listedWorkflowSteps.length}
        current={currentStepIndex}
        persistedStepIndexRef={workflowOptions?.stepProgressPersistedIndexRef}
      />
      <Column
        gap="0.5rem"
        style={{
          justifyContent: 'center',
          minHeight: dotsHeight,
          width: STEP_CONTENT_WIDTH,
          flex: '0 0 auto',
        }}
      >
        <Subtitle style={{ color: 'var(--palette-blue-300)' }}>
          {enrich(translations.stepLabel, stepNumber)}
        </Subtitle>
        <Title.H2 style={{ color: 'var(--brand-secondary)' }}>{title}</Title.H2>
        {description && (
          <Text.Medium
            color="var(--palette-blue-300)"
            style={{ maxWidth: '21rem' }}
          >
            {description}
          </Text.Medium>
        )}
      </Column>
    </Row>
  )

  return (
    <Column
      alignItems="center"
      style={{
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        flex: '0 1 auto',
      }}
    >
      {stepGuideHeader}
      <Row
        gap={STEP_GUIDE_GAP}
        alignItems="flex-start"
        style={{
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Column
          aria-hidden
          style={{
            flex: `1 0 ${STEP_GUIDE_WIDTH}`,
            maxWidth: STEP_GUIDE_WIDTH,
            minHeight: '1px',
          }}
        >
          <span />
        </Column>
        <Column
          alignItems="stretch"
          style={{
            flex: `1 0 ${STEP_CONTENT_WIDTH}`,
            maxWidth: STEP_CONTENT_WIDTH,
            width: '100%',
            minWidth: 0,
          }}
        >
          {children}
        </Column>
      </Row>
    </Column>
  )
}
